
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Smile } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import MentionAutocomplete from './MentionAutocomplete';
import EmojiPicker from './EmojiPicker';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  chatGroupId?: string;
  placeholder?: string;
}

const MessageInput = ({ value, onChange, onSubmit, chatGroupId, placeholder = "Type a message..." }: MessageInputProps) => {
  const [showMentions, setShowMentions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ start: 0, end: 0 });
  const [groupUsers, setGroupUsers] = useState<any[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ENHANCED: Load both group users and all available users for comprehensive mentions
  useEffect(() => {
    const loadUsers = async () => {
      if (!chatGroupId) return;
      
      try {
        console.log('🔄 Loading users for message input mentions...');
        
        // Get group users first
        const { data: groupUserIds } = await supabase
          .from('chat_group_users')
          .select('user_id')
          .eq('chat_group_id', chatGroupId)
          .eq('is_active', true);

        if (!groupUserIds?.length) {
          console.log('No group users found');
          return;
        }

        const userIds = groupUserIds.map(item => item.user_id);
        
        // Get profiles for group users (both Supabase and Internal)
        const [supabaseProfiles, internalUsers] = await Promise.all([
          supabase
            .from('profiles')
            .select('id, username, first_name, last_name')
            .in('id', userIds),
          supabase
            .from('internal_users')
            .select('id, username')
            .in('id', userIds)
        ]);

        const combinedUsers = [
          // Supabase users
          ...(supabaseProfiles.data || []).map(profile => ({
            id: profile.id,
            username: profile.username || profile.first_name || 'Unknown',
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            user_type: 'supabase'
          })),
          // Internal users
          ...(internalUsers.data || []).map(user => ({
            id: user.id,
            username: user.username || 'Unknown',
            first_name: user.username || '',
            last_name: '',
            user_type: 'internal'
          }))
        ];

        console.log(`✅ Loaded ${combinedUsers.length} users for message input:`, {
          supabase: supabaseProfiles.data?.length || 0,
          internal: internalUsers.data?.length || 0
        });

        setGroupUsers(combinedUsers);
      } catch (error) {
        console.error('❌ Error loading users for message input:', error);
      }
    };

    loadUsers();
  }, [chatGroupId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
    
    // Close emoji picker on Escape
    if (e.key === 'Escape' && showEmojiPicker) {
      setShowEmojiPicker(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    onChange(newValue);
    
    // ENHANCED: Check for @ mentions with better detection
    const textBeforeCursor = newValue.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      
      // Check if we're still in a mention (no spaces)
      if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
        console.log('🏷️ Mention query detected:', textAfterAt);
        setMentionQuery(textAfterAt);
        setMentionPosition({ start: lastAtIndex, end: cursorPosition });
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (user: any) => {
    console.log('✅ Mention selected:', user.username, 'Type:', user.user_type);
    
    const beforeMention = value.slice(0, mentionPosition.start);
    const afterMention = value.slice(mentionPosition.end);
    const username = user.username || user.first_name || 'Unknown';
    const newValue = `${beforeMention}@${username} ${afterMention}`;
    
    onChange(newValue);
    setShowMentions(false);
    
    // Focus back to textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
      const newCursorPosition = mentionPosition.start + username.length + 2;
      setTimeout(() => {
        textareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  };

  const handleEmojiSelect = (emoji: any) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const beforeCursor = value.slice(0, start);
    const afterCursor = value.slice(end);
    const newValue = beforeCursor + emoji.native + afterCursor;
    
    onChange(newValue);
    setShowEmojiPicker(false);
    
    // Focus back to textarea and position cursor after emoji
    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = start + emoji.native.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <div className="relative bg-gray-800">
      <form onSubmit={onSubmit} className="flex gap-3 items-end p-3">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 resize-none min-h-[48px] max-h-32 text-sm py-3 px-4 pr-12"
            rows={2}
          />
          
          {/* Emoji Button */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="absolute right-2 top-2 h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-600"
          >
            <Smile className="h-4 w-4" />
          </Button>
          
          {showMentions && (
            <MentionAutocomplete
              users={groupUsers}
              query={mentionQuery}
              onSelect={handleMentionSelect}
              onClose={() => setShowMentions(false)}
            />
          )}
          
          <EmojiPicker
            isOpen={showEmojiPicker}
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        </div>
        
        <Button
          type="submit"
          size="sm"
          disabled={!value.trim()}
          className="bg-sports-green hover:bg-sports-green/90 text-black h-10 w-10 p-0 flex-shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};

export default MessageInput;
